import { t, useI18n } from '../../i18n';
import React, { useMemo } from 'react';
import type { Shift, ShiftSegment } from '../../services/shiftService';
import './ShiftSegmentFlow.css';

interface ShiftSegmentFlowProps {
  shift: Shift;
  formatMinute: (minute: number) => string;
  formatDuration: (minutes: number) => string;
}

interface ClampedBreak {
  segment: ShiftSegment;
  startMinute: number;
  endMinute: number;
}

interface TimelinePiece {
  id: string;
  type: 'duty' | 'break';
  startMinute: number;
  endMinute: number;
  segment: ShiftSegment;
}

const DUTY_KIND_LABELS: Record<string, string> = {
  fixed_post: 'Post',
  patrol: 'Patrol',
  desk: 'Desk',
  mobile: 'Mobile',
  custom: 'Custom',
};

const overlaps = (left: ShiftSegment, right: ShiftSegment): boolean => (
  left.start_minute < right.end_minute && left.end_minute > right.start_minute
);

function segmentMetaLabel(segment: ShiftSegment): string {
  if (segment.segment_type === 'break') {
    return segment.is_paid ? 'Paid break' : 'Break';
  }

  if (segment.duty_kind && DUTY_KIND_LABELS[segment.duty_kind]) {
    return DUTY_KIND_LABELS[segment.duty_kind];
  }

  return t('Duty');
}

function buildDutyPieces(duty: ShiftSegment, breaks: ShiftSegment[]): TimelinePiece[] {
  const clampedBreaks: ClampedBreak[] = breaks
    .map((segment) => ({
      segment,
      startMinute: Math.max(duty.start_minute, segment.start_minute),
      endMinute: Math.min(duty.end_minute, segment.end_minute),
    }))
    .filter((segmentBreak) => segmentBreak.endMinute > segmentBreak.startMinute)
    .sort((left, right) => left.startMinute - right.startMinute || left.endMinute - right.endMinute);

  const pieces: TimelinePiece[] = [];
  let cursor = duty.start_minute;

  clampedBreaks.forEach((segmentBreak) => {
    const breakStart = Math.max(cursor, segmentBreak.startMinute);
    const breakEnd = Math.max(breakStart, segmentBreak.endMinute);

    if (breakStart > cursor) {
      pieces.push({
        id: `${duty.id}-duty-${cursor}-${breakStart}`,
        type: 'duty',
        startMinute: cursor,
        endMinute: breakStart,
        segment: duty,
      });
    }

    if (breakEnd > breakStart) {
      pieces.push({
        id: `${duty.id}-break-${segmentBreak.segment.id}-${breakStart}-${breakEnd}`,
        type: 'break',
        startMinute: breakStart,
        endMinute: breakEnd,
        segment: segmentBreak.segment,
      });
    }

    cursor = Math.max(cursor, breakEnd);
  });

  if (cursor < duty.end_minute) {
    pieces.push({
      id: `${duty.id}-duty-${cursor}-${duty.end_minute}`,
      type: 'duty',
      startMinute: cursor,
      endMinute: duty.end_minute,
      segment: duty,
    });
  }

  return pieces.filter((piece) => piece.endMinute > piece.startMinute);
}

function SegmentList({
  segments,
  formatMinute,
}: {
  segments: ShiftSegment[];
  formatMinute: (minute: number) => string;
}) {
  return (
    <div className="shift-segment-flow shift-segment-flow--list">
      {segments.map((segment) => (
        <div
          key={segment.id}
          className={`shift-segment-flow__segment${segment.segment_type === 'break' ? ' shift-segment-flow__segment--break' : ''}`}
        >
          <div className="shift-segment-flow__segment-row">
            <span className="shift-segment-flow__segment-time">
              {formatMinute(segment.start_minute)} - {formatMinute(segment.end_minute)}
            </span>
            <span className="shift-segment-flow__segment-label">{segment.label}</span>
          </div>

          <div className="shift-segment-flow__segment-meta">
            <span>{segmentMetaLabel(segment)}</span>
            {!segment.is_published && <span className="shift-segment-flow__segment-chip">{t('Draft')}</span>}
          </div>

          {segment.notes && (
            <p className="shift-segment-flow__segment-notes">{segment.notes}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ShiftSegmentFlow({
  shift,
  formatMinute,
  formatDuration,
}: ShiftSegmentFlowProps) {
  useI18n();
  const { duties, breaks, overlaidBreakIds, dutyBreaksById } = useMemo(() => {
    const dutySegments = shift.segments.filter((segment) => segment.segment_type === 'duty');
    const breakSegments = shift.segments.filter((segment) => segment.segment_type === 'break');
    const breakIds = new Set<string>();
    const breakMap = new Map<string, ShiftSegment[]>();

    if (shift.is_published) {
      dutySegments.forEach((duty) => {
        const matchingBreaks = breakSegments
          .filter((segmentBreak) => segmentBreak.is_published && overlaps(duty, segmentBreak))
          .sort((left, right) => left.start_minute - right.start_minute || left.end_minute - right.end_minute);

        if (matchingBreaks.length > 0) {
          breakMap.set(duty.id, matchingBreaks);
          matchingBreaks.forEach((segmentBreak) => breakIds.add(segmentBreak.id));
        }
      });
    }

    return {
      duties: dutySegments,
      breaks: breakSegments,
      overlaidBreakIds: breakIds,
      dutyBreaksById: breakMap,
    };
  }, [shift]);

  if (!shift.is_published || overlaidBreakIds.size === 0 || duties.length === 0) {
    return (
      <SegmentList
        segments={shift.segments}
        formatMinute={formatMinute}
      />
    );
  }

  const standaloneBreaks = breaks.filter((segmentBreak) => !overlaidBreakIds.has(segmentBreak.id));

  return (
    <div className="shift-segment-flow shift-segment-flow--overlay">
      {duties.map((duty) => {
        const dutyBreaks = dutyBreaksById.get(duty.id) ?? [];
        const pieces = buildDutyPieces(duty, dutyBreaks);

        return (
          <div key={duty.id} className="shift-segment-flow__duty">
            <div className="shift-segment-flow__duty-head">
              <span className="shift-segment-flow__segment-time">
                {formatMinute(duty.start_minute)} - {formatMinute(duty.end_minute)}
              </span>
              <span className="shift-segment-flow__segment-label">{duty.label}</span>
              <span className="shift-segment-flow__segment-chip">{segmentMetaLabel(duty)}</span>
            </div>

            <div className="shift-segment-flow__bar" aria-label={`${duty.label} with breaks shown inline`}>
              {pieces.map((piece) => {
                const duration = piece.endMinute - piece.startMinute;
                const isBreak = piece.type === 'break';
                const pieceLabel = isBreak ? segmentMetaLabel(piece.segment) : duty.label;

                return (
                  <span
                    key={piece.id}
                    className={`shift-segment-flow__piece shift-segment-flow__piece--${piece.type}`}
                    style={{ flex: `${duration} 1 0` }}
                    title={`${pieceLabel}: ${formatMinute(piece.startMinute)} - ${formatMinute(piece.endMinute)}`}
                  >
                    <span>{isBreak ? 'Break' : ''}</span>
                  </span>
                );
              })}
            </div>

            {dutyBreaks.length > 0 && (
              <div className="shift-segment-flow__inline-breaks">
                {dutyBreaks.map((segmentBreak) => (
                  <span key={segmentBreak.id}>
                    {segmentMetaLabel(segmentBreak)} {formatMinute(segmentBreak.start_minute)} - {formatMinute(segmentBreak.end_minute)}
                  </span>
                ))}
              </div>
            )}

            {duty.notes && (
              <p className="shift-segment-flow__segment-notes">{duty.notes}</p>
            )}
          </div>
        );
      })}

      {standaloneBreaks.map((segmentBreak) => (
        <div key={segmentBreak.id} className="shift-segment-flow__standalone-break">
          <span className="shift-segment-flow__segment-time">
            {formatMinute(segmentBreak.start_minute)} - {formatMinute(segmentBreak.end_minute)}
          </span>
          <span>{segmentMetaLabel(segmentBreak)}</span>
          <span>{formatDuration(segmentBreak.end_minute - segmentBreak.start_minute)}</span>
        </div>
      ))}
    </div>
  );
}
